import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { useState } from 'react';
import React from 'react';
import feathersClient from '../../../../apiClient';
import { VodafoneTariffPaymentData } from '@tymbe/schema/vodafone-tariff-payment.interface';
import { VodafoneTariffApplicationType, SimType, VodafoneTariffType, VodafoneTariffPaymentState, VodafoneTariffState } from '@tymbe/schema/enums';
import { SideModal } from '@tymbe/ty-components/modals';
import { Paragraph } from '@tymbe/ty-components/text';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { PrimaryButton, SecondaryButton } from '@tymbe/ui-primitives';
import { toast } from 'react-toastify';
import { useDocumentDownload } from '../../../../hooks/useDocumentDownload';
import { BucketObject } from '@tymbe/bucket';

type VodafoneTariffDetailModalProps = {
  tariffId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

const getApplicationTypeLabel = (type: VodafoneTariffApplicationType) => {
  switch (type) {
    case VodafoneTariffApplicationType.NEW_PHONE_NUMBER:
      return 'Nové číslo';
    case VodafoneTariffApplicationType.VODAFONE_TRANSFER:
      return 'Přenos z Vodafone';
    case VodafoneTariffApplicationType.DIFFERENT_OPERATOR_TRANSFER:
      return 'Přenos z jiného operátora';
    default:
      return type;
  }
};

const getSimTypeLabel = (type: SimType) => {
  switch (type) {
    case SimType.ESIM:
      return 'eSIM';
    case SimType.PHYSICAL_SIM:
      return 'Fyzická SIM';
    case SimType.EXISTING_SIM:
      return 'Stávající SIM';
    default:
      return type;
  }
};

const getTariffTypeLabel = (type: VodafoneTariffType) => {
  switch (type) {
    case 'TP2':
      return 'TP2';
    case 'TP3':
      return 'TP3';
    case 'TP4':
      return 'TP4';
    default:
      return type;
  }
};

// Utility function to format period for display as MM. YYYY
const formatPeriod = (period: string): string => {
  // Handle both old format (MM.YYYY) and new format (YYYY-MM-DD)
  if (period.includes('.')) {
    // Old format: 05.2025 -> 05. 2025
    return period.replace('.', '. ');
  } else {
    // New format: 2025-05-01 -> 05. 2025
    const date = new Date(period);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}. ${year}`;
  }
};

const getPaymentStateVariant = (state: VodafoneTariffPaymentState) => {
  switch (state) {
    case VodafoneTariffPaymentState.PAID:
      return 'success' as const;
    case VodafoneTariffPaymentState.PENDING_PAYMENT:
      return 'warning' as const;
    case VodafoneTariffPaymentState.PROCESSING_FAILED:
    case VodafoneTariffPaymentState.CANCELLED:
      return 'error' as const;
    case VodafoneTariffPaymentState.TO_BE_PROCESSED:
      return 'default' as const;
    default:
      return 'default' as const;
  }
};

const getPaymentStateLabel = (state: VodafoneTariffPaymentState) => {
  switch (state) {
    case VodafoneTariffPaymentState.TO_BE_PROCESSED:
      return 'Ke zpracování';
    case VodafoneTariffPaymentState.PROCESSING_FAILED:
      return 'Zpracování selhalo';
    case VodafoneTariffPaymentState.PENDING_PAYMENT:
      return 'Čeká na platbu';
    case VodafoneTariffPaymentState.PAID:
      return 'Zaplaceno';
    case VodafoneTariffPaymentState.CANCELLED:
      return 'Zrušeno';
    default:
      return state;
  }
};

const getTariffStateVariant = (state: VodafoneTariffState) => {
  switch (state) {
    case VodafoneTariffState.ACTIVE:
      return 'success' as const;
    case VodafoneTariffState.PAYMENT_SUCCESS:
      return 'success' as const;
    case VodafoneTariffState.PAYMENT_PENDING:
      return 'warning' as const;
    case VodafoneTariffState.REQUESTED_TRANSFER:
      return 'warning' as const;
    case VodafoneTariffState.CANCELLED:
    case VodafoneTariffState.DEACTIVATED:
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const getTariffStateLabel = (state: VodafoneTariffState) => {
  switch (state) {
    case VodafoneTariffState.PAYMENT_PENDING:
      return 'Čeká na platbu';
    case VodafoneTariffState.PAYMENT_SUCCESS:
      return 'Platba úspěšná';
    case VodafoneTariffState.ACTIVE:
      return 'Aktivní';
    case VodafoneTariffState.CANCELLED:
      return 'Zrušeno';
    case VodafoneTariffState.REQUESTED_TRANSFER:
      return 'Požadován přenos';
    case VodafoneTariffState.DEACTIVATED:
      return 'Deaktivováno';
    default:
      return state;
  }
};

const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
  }).format(amount);
};

const FakturoidLink = ({ id, type }: { id: string | number | null; type: 'invoice' | 'subject' }) => {
  if (!id) return <span>N/A</span>;
  
  const baseUrl = 'https://app.fakturoid.cz/tybrigady2';
  const url = type === 'invoice' 
    ? `${baseUrl}/invoices/${id}`
    : `${baseUrl}/subjects/${id}`;
  
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 underline transition-colors"
    >
      {id}
    </a>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string | number | null | React.ReactNode }) => (
  <div className="flex justify-between items-center py-2 border-b border-special-outline-divider-transparent last:border-b-0">
    <Paragraph variant="captionRg" className="text-gray-600 font-medium">{label}:</Paragraph>
    <Paragraph variant="body2Rg" className="text-right">
      {value || 'N/A'}
    </Paragraph>
  </div>
);

const PaymentCard = ({ payment }: { payment: VodafoneTariffPaymentData }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex justify-between items-center mb-3">
      <Paragraph variant="body2Md" className="text-gray-900 font-semibold">
        Období {formatPeriod(payment.period_month)}
      </Paragraph>
      <BadgePill variant={getPaymentStateVariant(payment.state)}>
        {getPaymentStateLabel(payment.state)}
      </BadgePill>
    </div>
    
    <div className="space-y-2">
      {payment.invoice_id && (
        <div className="flex justify-between items-center py-1">
          <Paragraph variant="captionRg" className="text-gray-600">ID faktury:</Paragraph>
          <Paragraph variant="body2Rg" className="text-gray-900 font-medium">
            <FakturoidLink id={payment.invoice_id} type="invoice" />
          </Paragraph>
        </div>
      )}
      <div className="flex justify-between items-center py-1">
        <Paragraph variant="captionRg" className="text-gray-600">Vytvořeno:</Paragraph>
        <Paragraph variant="body2Rg" className="text-gray-900 font-medium">
          {moment(payment.created_at).format('DD.MM.YYYY HH:mm')}
        </Paragraph>
      </div>
    </div>
  </div>
);

export const VodafoneTariffDetailModal = ({ 
  tariffId, 
  isOpen, 
  onClose 
}: VodafoneTariffDetailModalProps) => {
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [phoneNumberValue, setPhoneNumberValue] = useState('');
  const [simTypeValue, setSimTypeValue] = useState<SimType | ''>('');
  const [applicationTypeValue, setApplicationTypeValue] = useState<VodafoneTariffApplicationType | ''>('');
  const [okuCodeValue, setOkuCodeValue] = useState('');
  const [eidValue, setEidValue] = useState('');
  const [physicalSimCodeValue, setPhysicalSimCodeValue] = useState('');
  const [requestedTariffValue, setRequestedTariffValue] = useState<VodafoneTariffType | ''>('');
  const queryClient = useQueryClient();

  const handleClose = () => {
    setIsEditingNote(false);
    setIsEditingDetails(false);
    onClose();
  };

  const { data: tariff, isLoading: isLoadingTariff } = useQuery(
    ['vodafone-tariff-detail', tariffId],
    async () => {
      if (!tariffId) return null;
      return feathersClient.service('vodafone-tariff').get(tariffId);
    },
    {
      enabled: !!tariffId && isOpen,
      onSuccess: (data) => {
        if (data) {
          setNoteValue(data.note || '');
          setPhoneNumberValue(data.phone_number || '');
          setSimTypeValue(data.sim_type || '');
          setApplicationTypeValue(data.application_type || '');
          setOkuCodeValue(data.oku_code || '');
          setEidValue(data.eid || '');
          setPhysicalSimCodeValue(data.physical_sim_code || '');
          setRequestedTariffValue(data.requested_tariff || '');
        }
      }
    }
  );

  const { mutateAsync: updateNote, isLoading: isUpdatingNote } = useMutation(
    async (note: string) => {
      if (!tariffId) throw new Error('No tariff ID');
      return feathersClient.service('vodafone-tariff').patch(tariffId, { note });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vodafone-tariff-detail', tariffId]);
        // Invalidate table queries to refresh note display
        queryClient.invalidateQueries('admin/vodafoneTariff');
        setIsEditingNote(false);
        toast.success('Poznámka byla úspěšně uložena');
      },
      onError: () => {
        toast.error('Chyba při ukládání poznámky');
      },
    }
  );

  const { mutateAsync: updateDetails, isLoading: isUpdatingDetails } = useMutation(
    async (details: { 
      phone_number: string; 
      sim_type: SimType; 
      application_type: VodafoneTariffApplicationType;
      oku_code: string;
      eid?: string;
      physical_sim_code?: string;
      requested_tariff: VodafoneTariffType;
    }) => {
      if (!tariffId) throw new Error('No tariff ID');
      return feathersClient.service('vodafone-tariff').patch(tariffId, details);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vodafone-tariff-detail', tariffId]);
        queryClient.invalidateQueries('admin/vodafoneTariff');
        setIsEditingDetails(false);
        toast.success('Detaily byly úspěšně uloženy');
      },
      onError: () => {
        toast.error('Chyba při ukládání detailů');
      },
    }
  );

  const { data: payments, isLoading: isLoadingPayments } = useQuery(
    ['vodafone-tariff-payments', tariffId],
    async () => {
      if (!tariffId) return [];
      const result = await feathersClient.service('vodafone-tariff-payment').find({
        query: {
          tariff_application_id: tariffId,
          $sort: { period_month: -1 }
        }
      });
      return result.data || [];
    },
    {
      enabled: !!tariffId && isOpen,
    }
  );

  const { downloadDocument } = useDocumentDownload();

  const { data: pdfFiles, isLoading: isLoadingPdfs } = useQuery(
    ['vodafone-tariff-pdfs', tariff?.fakturoid_proforma_id],
    async () => {
      const invoiceId = tariff?.fakturoid_proforma_id;
      const files = await feathersClient.service('bucket').find({
        query: {
          prefix: 'vodafone-tariff/',
          matchPattern: `vodafone_transfer_${invoiceId}_`
        }
      }) as BucketObject[];
      return {
        regular: files.find((file) => !file.name.includes('_EDITABLE')),
        editable: files.find((file) => file.name.includes('_EDITABLE'))
      };
    },
    {
      enabled: !!tariff?.fakturoid_proforma_id &&
               tariff?.application_type === VodafoneTariffApplicationType.VODAFONE_TRANSFER &&
               isOpen,
    }
  );

  const handleSaveNote = async () => {
    try {
      await updateNote(noteValue);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCancelEdit = () => {
    setNoteValue(tariff?.note || '');
    setIsEditingNote(false);
  };

  const handleSaveDetails = async () => {
    try {
      const details: any = {
        phone_number: phoneNumberValue,
        sim_type: simTypeValue as SimType,
        application_type: applicationTypeValue as VodafoneTariffApplicationType,
        oku_code: okuCodeValue,
        requested_tariff: requestedTariffValue as VodafoneTariffType
      };
      
      // Only include EID if sim type is eSIM
      if (simTypeValue === SimType.ESIM) {
        details.eid = eidValue;
      }
      
      // Only include physical_sim_code if sim type is PHYSICAL_SIM
      if (simTypeValue === SimType.PHYSICAL_SIM) {
        details.physical_sim_code = physicalSimCodeValue;
      }
      
      await updateDetails(details);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCancelDetailsEdit = () => {
    setPhoneNumberValue(tariff?.phone_number || '');
    setSimTypeValue(tariff?.sim_type || '');
    setApplicationTypeValue(tariff?.application_type || '');
    setOkuCodeValue(tariff?.oku_code || '');
    setEidValue(tariff?.eid || '');
    setPhysicalSimCodeValue(tariff?.physical_sim_code || '');
    setRequestedTariffValue(tariff?.requested_tariff || '');
    setIsEditingDetails(false);
  };

  const canEditDetails = tariff && (
    tariff.state === VodafoneTariffState.PAYMENT_PENDING ||
    tariff.state === VodafoneTariffState.PAYMENT_SUCCESS ||
    tariff.state === VodafoneTariffState.REQUESTED_TRANSFER
  );

  if (!isOpen || !tariffId) {
    return null;
  }

  return (
    <SideModal
      title="Detail Vodafone tarifu"
      className="w-[600px]"
      isOpen={isOpen}
      onRequestClose={handleClose}
    >
      <div className="p-6 max-h-full overflow-y-auto">
        {isLoadingTariff ? (
          <div className="flex justify-center py-8">
            <Paragraph variant="captionRg">Načítám...</Paragraph>
          </div>
        ) : tariff ? (
          <>
            {/* State Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <Paragraph variant="body1Md" className="text-gray-900">
                  Stav tarifu
                </Paragraph>
                <BadgePill variant={getTariffStateVariant(tariff.state)}>
                  {getTariffStateLabel(tariff.state)}
                </BadgePill>
              </div>
            </div>

            {/* Basic Info Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <Paragraph variant="body1Md" className="text-gray-900 mb-4">
                Základní informace
              </Paragraph>
              <div className="space-y-2">
                <DetailRow label="ID" value={tariff.id} />
                <DetailRow label="Person ID" value={tariff.person_id} />
                <DetailRow label="Jméno" value={`${tariff.first_name} ${tariff.last_name}`} />
                <DetailRow label="Email" value={tariff.email} />
                <DetailRow label="Datum narození" value={moment(tariff.birth_date).format('DD.MM.YYYY')} />
                <DetailRow label="Kontaktní telefon" value={tariff.contact_phone} />
              </div>
            </div>

            {/* Tariff Details Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-4">
                <Paragraph variant="body1Md" className="text-gray-900">
                  Detaily tarifu
                </Paragraph>
                {canEditDetails && !isEditingDetails && (
                  <SecondaryButton
                    onClick={() => setIsEditingDetails(true)}
                  >
                    Upravit
                  </SecondaryButton>
                )}
              </div>
              
              {isEditingDetails ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefonní číslo
                    </label>
                    <input
                      type="text"
                      value={phoneNumberValue}
                      onChange={(e) => setPhoneNumberValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Telefonní číslo"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Typ aplikace
                    </label>
                    <select
                      value={applicationTypeValue}
                      onChange={(e) => setApplicationTypeValue(e.target.value as VodafoneTariffApplicationType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled={!!applicationTypeValue}>Vyberte typ aplikace</option>
                      <option value={VodafoneTariffApplicationType.NEW_PHONE_NUMBER}>Nové číslo</option>
                      <option value={VodafoneTariffApplicationType.VODAFONE_TRANSFER}>Přenos z Vodafone</option>
                      <option value={VodafoneTariffApplicationType.DIFFERENT_OPERATOR_TRANSFER}>Přenos z jiného operátora</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Typ SIM
                    </label>
                    <select
                      value={simTypeValue}
                      onChange={(e) => setSimTypeValue(e.target.value as SimType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled={!!simTypeValue}>Vyberte typ SIM</option>
                      <option value={SimType.ESIM}>eSIM</option>
                      <option value={SimType.PHYSICAL_SIM}>Fyzická SIM</option>
                      <option value={SimType.EXISTING_SIM}>Stávající SIM</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OKU kód
                    </label>
                    <input
                      type="text"
                      value={okuCodeValue}
                      onChange={(e) => setOkuCodeValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="OKU kód"
                    />
                  </div>

                  {simTypeValue === SimType.ESIM && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        EID
                      </label>
                      <input
                        type="text"
                        value={eidValue}
                        onChange={(e) => setEidValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="EID"
                      />
                    </div>
                  )}

                  {simTypeValue === SimType.PHYSICAL_SIM && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kód fyzické SIM
                      </label>
                      <input
                        type="text"
                        value={physicalSimCodeValue}
                        onChange={(e) => setPhysicalSimCodeValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Kód fyzické SIM"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Požadovaný tarif
                    </label>
                    <select
                      value={requestedTariffValue}
                      onChange={(e) => setRequestedTariffValue(e.target.value as VodafoneTariffType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled={!!requestedTariffValue}>Vyberte tarif</option>
                      <option value="TP2">TP2</option>
                      <option value="TP3">TP3</option>
                      <option value="TP4">TP4</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <SecondaryButton 
                      onClick={handleCancelDetailsEdit}
                      disabled={isUpdatingDetails}
                    >
                      Zrušit
                    </SecondaryButton>
                    <PrimaryButton 
                      onClick={handleSaveDetails}
                      disabled={isUpdatingDetails}
                    >
                      {isUpdatingDetails ? 'Ukládám...' : 'Uložit'}
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <DetailRow label="Telefonní číslo" value={tariff.phone_number} />
                  <DetailRow label="Typ aplikace" value={getApplicationTypeLabel(tariff.application_type)} />
                  <DetailRow label="Typ SIM" value={getSimTypeLabel(tariff.sim_type)} />
                  {tariff.sim_type === SimType.ESIM && (
                    <DetailRow label="EID" value={tariff.eid} />
                  )}
                  {tariff.sim_type === SimType.PHYSICAL_SIM && (
                    <DetailRow label="Kód fyzické SIM" value={tariff.physical_sim_code} />
                  )}
                  <DetailRow label="Požadovaný tarif" value={getTariffTypeLabel(tariff.requested_tariff)} />
                  <DetailRow label="OKU kód" value={tariff.oku_code} />
                  <DetailRow label="Datum přenosu" value={tariff.transfer_date ? moment(tariff.transfer_date).format('DD.MM.YYYY') : null} />
                </div>
              )}
            </div>

            {/* Fakturoid Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <Paragraph variant="body1Md" className="text-gray-900 mb-4">
                Fakturace
              </Paragraph>
              <div className="space-y-2">
                <DetailRow 
                  label="Fakturoid Subject ID" 
                  value={<FakturoidLink id={tariff.fakturoid_subject_id} type="subject" />} 
                />
                <DetailRow 
                  label="Fakturoid Proforma ID" 
                  value={<FakturoidLink id={tariff.fakturoid_proforma_id} type="invoice" />} 
                />
              </div>
            </div>

            {/* Documents Section - Only for Vodafone Transfer applications */}
            {tariff.application_type === VodafoneTariffApplicationType.VODAFONE_TRANSFER && tariff.fakturoid_proforma_id && (
              <div className="bg-secondary-50 rounded-lg p-4 mb-4">
                <Paragraph variant="body1Md" className="text-gray-900 mb-4">
                  Dokumenty
                </Paragraph>
                {isLoadingPdfs ? (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <Paragraph variant="captionRg">Načítám dokumenty...</Paragraph>
                  </div>
                ) : pdfFiles?.regular || pdfFiles?.editable ? (
                  <div className="flex flex-col gap-2">
                    {pdfFiles.regular && (
                      <SecondaryButton
                        onClick={() => downloadDocument(pdfFiles.regular!.name, `vodafone_transfer_${tariff.fakturoid_proforma_id}.pdf`)}
                        className="w-full"
                      >
                        Stáhnout Vodafone PDF
                      </SecondaryButton>
                    )}
                    {pdfFiles.editable && (
                      <SecondaryButton
                        onClick={() => downloadDocument(pdfFiles.editable!.name, `vodafone_transfer_${tariff.fakturoid_proforma_id}_EDITABLE.pdf`)}
                        className="w-full"
                      >
                        Stáhnout editovatelné Vodafone PDF
                      </SecondaryButton>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <Paragraph variant="captionRg" className="text-gray-500">Žádné dokumenty nenalezeny</Paragraph>
                  </div>
                )}
              </div>
            )}

            {/* Note Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-4">
                <Paragraph variant="body1Md" className="text-gray-900">
                  Poznámka
                </Paragraph>
                {!isEditingNote && (
                  <SecondaryButton
                    onClick={() => setIsEditingNote(true)}
                  >
                    Upravit
                  </SecondaryButton>
                )}
              </div>

              {isEditingNote ? (
                <div className="space-y-4">
                  <textarea
                    value={noteValue}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNoteValue(e.target.value)}
                    placeholder="Zadejte poznámku..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  />
                  <div className="flex gap-2 justify-end">
                    <SecondaryButton 
                      onClick={handleCancelEdit}
                      disabled={isUpdatingNote}
                    >
                      Zrušit
                    </SecondaryButton>
                    <PrimaryButton 
                      onClick={handleSaveNote}
                      disabled={isUpdatingNote}
                    >
                      {isUpdatingNote ? 'Ukládám...' : 'Uložit'}
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <div className="min-h-[60px] p-3 bg-white rounded border border-gray-200">
                  <Paragraph variant="body2Rg" className="whitespace-pre-wrap">
                    {tariff.note || 'Žádná poznámka'}
                  </Paragraph>
                </div>
              )}
            </div>

            {/* Timestamps Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-4">
              <Paragraph variant="body1Md" className="text-gray-900 mb-4">
                Časové údaje
              </Paragraph>
              <div className="space-y-2">
                <DetailRow label="Vytvořeno" value={moment(tariff.created_at).format('DD.MM.YYYY HH:mm')} />
                <DetailRow label="Aktualizováno" value={moment(tariff.updated_at).format('DD.MM.YYYY HH:mm')} />
                {tariff.transfer_requested && (
                  <DetailRow label="Přenos požádán" value={moment(tariff.transfer_requested).format('DD.MM.YYYY HH:mm')} />
                )}
                {tariff.deleted_at && (
                  <DetailRow label="Smazáno" value={moment(tariff.deleted_at).format('DD.MM.YYYY HH:mm')} />
                )}
              </div>
            </div>

            {/* Payments Section */}
            <div className="bg-secondary-50 rounded-lg p-4 mb-6">
              <Paragraph variant="body1Md" className="text-gray-900 mb-4">
                Platby ({isLoadingPayments ? 'načítám...' : payments?.length || 0})
              </Paragraph>
              {isLoadingPayments ? (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <Paragraph variant="captionRg">Načítám platby...</Paragraph>
                </div>
              ) : payments && payments.length > 0 ? (
                <div>
                  {payments.map((payment: VodafoneTariffPaymentData) => (
                    <PaymentCard key={payment.id} payment={payment} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <Paragraph variant="captionRg" className="text-gray-500">Žádné platby nenalezeny</Paragraph>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex justify-center py-8">
            <Paragraph variant="captionRg" className="text-red-600">Nepodařilo se načíst detail tarifu</Paragraph>
          </div>
        )}
      </div>
    </SideModal>
  );
};
